<template>
  <div
    v-if="isVisible"
    class="flex flex-col rounded-lg border border-lightGray30 px-3 py-3 text-left md:px-4"
    data-testid="station-records-section"
  >
    <CollapsibleSection
      title="Registros do plantão"
      test-id="station-records-accordion"
      :touch-target="touchTarget"
      expand-label="Ver registros do plantão"
      collapse-label="Ocultar registros do plantão"
    >
      <div class="flex flex-col gap-3">
        <span class="text-sm text-gray-500" data-testid="station-records-count">
          {{ countLabel }}
        </span>

        <div
          v-if="loadError"
          class="flex flex-col items-start gap-1 text-sm text-negative"
          data-testid="station-records-error"
        >
          <span>{{ loadError }}</span>
          <button
            type="button"
            class="font-semibold underline"
            data-testid="station-records-retry-button"
            @click="fetchRecords"
          >
            Tentar novamente
          </button>
        </div>

        <article
          v-for="record in records"
          :key="record.id"
          class="rounded border border-gray-30 bg-white"
          :data-testid="`station-record-card-${record.id}`"
        >
          <header
            class="flex flex-wrap items-center gap-2 border-b border-gray-30 px-3 py-2 text-sm"
          >
            <span class="font-bold">{{ kindLabel(record) }}</span>
            <span class="text-gray-500">#{{ record.id }}</span>
            <LabelBadge
              :label="statusLabel(record)"
              :color="statusColor(record)"
              size="small"
              :icon="null"
            />
            <LabelBadge
              v-if="record.overwritten !== 'NONE'"
              :label="record.overwritten === 'TOTAL' ? 'Sobrescrito' : 'Sobrescrito parcialmente'"
              color="yellow"
              size="small"
              :icon="null"
              :data-testid="`station-record-overwritten-${record.id}`"
            />
          </header>

          <div class="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2 text-xs md:grid-cols-4">
            <div class="flex flex-col">
              <span class="font-bold uppercase text-gray-500">Pernas cobertas</span>
              <span>{{ coveredLabel(record) }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold uppercase text-gray-500">Check-in</span>
              <span>{{ timeLabel(record.checkin) }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold uppercase text-gray-500">Check-out</span>
              <span>{{ timeLabel(record.checkout) }}</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold uppercase text-gray-500">Vale no horário efetivo</span>
              <span :data-testid="`station-record-wins-${record.id}`">{{ winsLabel(record) }}</span>
            </div>
          </div>

          <p
            v-if="lostLegsLabel(record)"
            class="px-3 pb-2 text-xs text-gray-500"
            :data-testid="`station-record-lost-${record.id}`"
          >
            {{ lostLegsLabel(record) }}
          </p>

          <footer
            class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-gray-30 px-3 py-2 text-xs"
          >
            <button
              type="button"
              class="font-semibold text-primary30 underline"
              :data-testid="`station-record-history-toggle-${record.id}-button`"
              :aria-expanded="openHistoryId === record.id ? 'true' : 'false'"
              @click="toggleHistory(record.id)"
            >
              {{ openHistoryId === record.id ? 'Ocultar histórico' : 'Ver histórico' }}
            </button>
            <button
              type="button"
              class="font-semibold text-primary30 underline"
              :data-testid="`station-record-open-${record.id}-button`"
              @click="openRecord(record)"
            >
              Abrir registro
            </button>
          </footer>

          <div
            v-if="openHistoryId === record.id"
            class="px-3 pb-3"
            :data-testid="`station-record-history-${record.id}`"
          >
            <span v-if="historyLoadingId === record.id" class="text-sm text-gray-500">
              Carregando histórico…
            </span>
            <DsAttendanceHistoryCard
              v-else
              title="Histórico do registro"
              :events="historyByRecord[record.id] ?? []"
            />
          </div>
        </article>
      </div>
    </CollapsibleSection>
  </div>
</template>

<script setup lang="ts">
/**
 * Spec 2026-09-24 §7.1/§8.3 (Q14) — "Registros do plantão": TODOS os registros (folha e
 * atividade) do plantão, quem vence cada perna no horário efetivo, fixação e sobrescrita
 * (com "vale pelo #Y" de `overwrittenBy`). Horários no `timezone` (offset) do envelope.
 * Convive com "Registros de Atividade da vaga" (anexos, solicitações, Enviar registro) — D-W4.
 */
import { computed, ref, watch } from 'vue'
import CollapsibleSection from './CollapsibleSection.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import DsAttendanceHistoryCard from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import Network from '@/Network'
import type {
  DoctorStationRecord,
  LegKey
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'
import {
  DS_ATTENDANCE_STATUS_LABELS,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import { pathForStatus } from '@/modules/company/Approvals/DsAttendance/utils/status-route-map'
import {
  LEG_LABELS,
  RECORD_KIND_LABELS,
  formatTimeInZone
} from '@/modules/company/Approvals/DsAttendance/utils/leg-overwrite'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'
import { mapAuditTrailToHistoryEvents, type HistoryEvent } from '@/utils/dsAttendanceHistory'

/** "Esta seção não existe para você" (sem acesso ou backend anterior), não "deu ruim". */
const SILENT_ERROR_STATUSES = [401, 403, 404, 405, 501]
const LEGS: LegKey[] = ['CHECKIN', 'CHECKOUT']

const props = withDefaults(
  defineProps<{
    doctorStationId: number | null
    /** Alvo de toque de 40px no cabeçalho do acordeão (celular, R6). */
    touchTarget?: boolean
  }>(),
  { touchTarget: false }
)

const { notifyError } = useErrorToast()

const records = ref<DoctorStationRecord[]>([])
const timezone = ref<string | null>(null)
const hasLoaded = ref(false)
const loadError = ref<string | null>(null)
const openHistoryId = ref<number | null>(null)
const historyLoadingId = ref<number | null>(null)
const historyByRecord = ref<Record<number, HistoryEvent[]>>({})

const isVisible = computed(
  () => hasLoaded.value && (records.value.length > 0 || loadError.value !== null)
)

const countLabel = computed(() =>
  records.value.length === 1 ? '1 registro' : `${records.value.length} registros`
)

/** Só a resposta da última chamada vale — troca de plantão no meio do fetch não sobrescreve. */
let fetchSeq = 0

async function fetchRecords() {
  const seq = ++fetchSeq
  const doctorStationId = props.doctorStationId
  loadError.value = null
  if (!doctorStationId) {
    records.value = []
    hasLoaded.value = true
    return
  }
  try {
    const response = await Network.dsAttendance.getDoctorStationRecords(doctorStationId)
    if (seq !== fetchSeq) return
    timezone.value = response.timezone
    records.value = response.records
  } catch (error) {
    if (seq !== fetchSeq) return
    records.value = []
    if (SILENT_ERROR_STATUSES.includes(getApiErrorStatus(error) ?? 0)) {
      console.debug('[StationRecords] sem acesso ou rota ausente; seção omitida', error)
    } else {
      console.error('Erro ao listar os registros do plantão:', error)
      loadError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar os registros deste plantão.'
      )
    }
  } finally {
    if (seq === fetchSeq) hasLoaded.value = true
  }
}

function kindLabel(record: Pick<DoctorStationRecord, 'kind'>): string {
  return RECORD_KIND_LABELS[record.kind] ?? record.kind
}

function statusLabel(record: DoctorStationRecord): string {
  return DS_ATTENDANCE_STATUS_LABELS[record.status as DSAttendanceStatus] ?? record.status
}

function statusColor(record: DoctorStationRecord): LabelBadgeColor {
  return record.status === DSAttendanceStatus.APPROVED ? 'green' : 'orange'
}

/** FOLHA cobre sempre as duas pernas; `coveredLegs` nulo = as duas (§3). */
function coveredLegsOf(record: DoctorStationRecord): LegKey[] {
  if (record.kind === 'FOLHA' || !record.coveredLegs || record.coveredLegs === 'BOTH') return LEGS
  return [record.coveredLegs]
}

function coveredLabel(record: DoctorStationRecord): string {
  const legs = coveredLegsOf(record)
  return legs.length === 2 ? 'Ambas' : LEG_LABELS[legs[0]]
}

function timeLabel(iso: string | null): string {
  return formatTimeInZone(iso, timezone.value) ?? '—'
}

function winsLabel(record: DoctorStationRecord): string {
  if (!record.winsLegs.length) return 'Nenhuma perna'
  return record.winsLegs
    .map((leg) => `${LEG_LABELS[leg]}${record.pinnedLegs.includes(leg) ? ' (fixada)' : ''}`)
    .join(' · ')
}

function overwriterLabel(containerId: number): string {
  const overwriter = records.value.find((other) => other.id === containerId)
  return overwriter
    ? `pelo ${kindLabel(overwriter)} #${containerId}`
    : `pelo registro #${containerId}`
}

/**
 * Q6 — "sobrescrito por #Y", direto de `overwrittenBy` (§7.1). Perna coberta, perdida e sem
 * entrada em `overwrittenBy` foi vencida por fonte fora dos registros (app/permanência).
 */
function lostLegsLabel(record: DoctorStationRecord): string | null {
  if (record.status !== DSAttendanceStatus.APPROVED) return null
  const lost = coveredLegsOf(record).filter((leg) => !record.winsLegs.includes(leg))
  if (!lost.length) return null
  const byOverwriter = new Map<string, LegKey[]>()
  for (const leg of lost) {
    const entry = (record.overwrittenBy ?? []).find((item) => item.leg === leg)
    const key = entry ? overwriterLabel(entry.containerId) : 'por outra fonte do plantão'
    byOverwriter.set(key, [...(byOverwriter.get(key) ?? []), leg])
  }
  return [...byOverwriter.entries()]
    .map(([who, legs]) => {
      const names = legs.map((leg) => LEG_LABELS[leg]).join(' e ')
      return `${names} ${legs.length > 1 ? 'valem' : 'vale'} ${who}`
    })
    .join(' · ')
}

async function toggleHistory(recordId: number) {
  if (openHistoryId.value === recordId) {
    openHistoryId.value = null
    return
  }
  openHistoryId.value = recordId
  if (historyByRecord.value[recordId]) return
  historyLoadingId.value = recordId
  try {
    const response = await Network.dsAttendance.getAuditTrail(recordId)
    historyByRecord.value = {
      ...historyByRecord.value,
      [recordId]: mapAuditTrailToHistoryEvents(response.data)
    }
  } catch (error) {
    notifyError(error, 'Não foi possível carregar o histórico do registro.')
  } finally {
    historyLoadingId.value = null
  }
}

/** Nova aba — o gestor não perde o plantão que está analisando. */
function openRecord(record: DoctorStationRecord) {
  window.open(pathForStatus(record.id, record.status), '_blank', 'noopener,noreferrer')
}

watch(
  () => props.doctorStationId,
  () => {
    hasLoaded.value = false
    records.value = []
    timezone.value = null
    openHistoryId.value = null
    historyByRecord.value = {}
    fetchRecords()
  },
  { immediate: true }
)

defineExpose({ fetchRecords })
</script>
